import React, { useState, useEffect } from 'react';
import axios from 'axios';
import NotificationBell from '../components/NotificationBell';
import OnboardingTour from '../components/OnboardingTour';

const api = () => axios.create({
 baseURL: '/api',
 headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const TABS = [
 ['overview', ' Overview'],
 ['users', ' Users'],
 ['shifts', ' Shifts'],
 ['reports', ' Reports'],
 ['metrics', ' Metrics'],
];

export default function AdminDashboard({ user, onLogout }) {
 const http = api();
 const [stats, setStats] = useState(null);
 const [shifts, setShifts] = useState([]);
 const [loading, setLoading] = useState(true);
 const [tab, setTab] = useState('overview');
 const [metrics, setMetrics] = useState(null);
 const [pendingCerts, setPendingCerts] = useState([]);
 const [certAction, setCertAction] = useState(null);

 const userData = user || JSON.parse(localStorage.getItem('user') || '{}');

 useEffect(() => { loadData(); }, []);

 // Load metrics when metrics tab is selected
 // eslint-disable-next-line react-hooks/exhaustive-deps
 useEffect(() => {
   if (tab === 'metrics' && !metrics) {
     loadMetrics();
   }
 }, [tab, metrics]);

 const loadMetrics = async () => {
 try {
 const m = await http.get('/admin/metrics');
 setMetrics(m.data);
 } catch { /* silent */ }
 };

 const handleCertVerify = async (id, approved) => {
 setCertAction(id);
 try {
 await http.put(`/admin/certifications/${id}/verify`, { approved });
 setPendingCerts(prev => prev.filter(c => c.id !== id));
 } catch { /* silent */ }
 setCertAction(null);
 };

 const loadData = async () => {
 setLoading(true);
 try {
 const [s, sh] = await Promise.all([http.get('/admin/stats'), http.get('/shifts')]);
 setStats(s.data || {});
 setShifts(sh.data || []);
 } catch { /* silent */ }
 setLoading(false);
 };

 const handleLogout = () => {
 localStorage.removeItem('token');
 localStorage.removeItem('user');
 if (onLogout) onLogout();
 };

 const healthScore = stats
 ? Math.min(100, Math.round(
 ((stats.total_workers || 0) * 2 +
 (stats.total_employers || 0) * 5 +
 (stats.total_completed || 0) * 10) / 2
 ))
 : 0;

 const fillRate = stats && (stats.total_applications || 0) > 0
 ? Math.round(((stats.total_completed || 0) / stats.total_applications) * 100)
 : 0;

 const workerEmployerRatio = stats && (stats.total_employers || 0) > 0
 ? (stats.total_workers / stats.total_employers).toFixed(1)
 : '—';

 return (
 <div className="min-h-screen bg-neutral-50">
 <OnboardingTour role="admin" />
 {/* ── HEADER ── */}
 <div className="bg-white border-b border-neutral-200 shadow-sm sticky top-0 z-10">
 <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
 <div className="flex items-center gap-3">
 <img src="/logo-icon.svg" alt="" className="w-7 h-7" />
 <div>
 <span className="font-brand font-bold text-neutral-900">ShiftMatch</span>
 <span className="ml-2 text-xs text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full font-semibold">Admin</span>
 </div>
 </div>
 <div className="flex items-center gap-3">
 <button
 onClick={loadData}
 className="text-xs text-neutral-400 hover:text-brand-600 transition px-3 py-1.5 rounded-lg hover:bg-brand-50 border border-transparent hover:border-brand-100"
 >
 ↻ Refresh
 </button>
 <NotificationBell user={userData} />
 <span className="text-sm text-neutral-600 hidden sm:block">{userData?.name || 'Admin'}</span>
 <button
 onClick={handleLogout}
 className="text-xs text-neutral-400 hover:text-red-500 transition px-3 py-1.5 rounded-lg hover:bg-red-50 border border-transparent hover:border-red-100"
 >
 Sign out
 </button>
 </div>
 </div>
 </div>

 <div className="max-w-5xl mx-auto px-4 py-6">
 <div className="mb-6">
 <h1 className="text-2xl font-bold text-neutral-900">Platform Overview</h1>
 <p className="text-neutral-500 text-sm mt-1">Real-time ShiftMatch health &amp; activity</p>
 </div>

 {/* ── TABS ── */}
 <div className="flex gap-1 bg-white p-1 rounded-xl mb-6 border border-neutral-100 shadow-sm">
 {TABS.map(([id, label]) => (
 <button key={id} onClick={() => setTab(id)}
 className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition ${
 tab === id
 ? 'bg-brand-500 text-white shadow-sm'
 : 'text-neutral-500 hover:text-neutral-800 hover:bg-neutral-50'
 }`}>
 {label}
 </button>
 ))}
 </div>

 {loading ? (
 <div className="flex justify-center py-20">
 <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
 </div>
 ) : (
 <>
 {/* ══ OVERVIEW TAB ══ */}
 {tab === 'overview' && (
 <>
 {/* Main stats */}
 <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
 {[
 { label: 'Workers', value: stats?.total_workers || 0, icon: '', valClass: 'text-blue-600', bg: 'bg-blue-50 border-blue-100' },
 { label: 'Employers', value: stats?.total_employers || 0, icon: '', valClass: 'text-purple-600', bg: 'bg-purple-50 border-purple-100' },
 { label: 'Shifts', value: stats?.total_shifts || 0, icon: '', valClass: 'text-brand-600', bg: 'bg-brand-50 border-brand-100' },
 { label: 'Applications', value: stats?.total_applications || 0, icon: '', valClass: 'text-amber-600', bg: 'bg-amber-50 border-amber-100' },
 { label: 'Completed', value: stats?.total_completed || 0, icon: '✅', valClass: 'text-brand-600', bg: 'bg-brand-50 border-brand-100' },
 ].map(s => (
 <div key={s.label} className={`rounded-2xl p-4 border ${s.bg} bg-white shadow-sm`}>
 <div className="text-2xl mb-2">{s.icon}</div>
 <div className={`text-2xl font-bold ${s.valClass}`}>{s.value.toLocaleString()}</div>
 <div className="text-xs text-neutral-500 mt-0.5">{s.label}</div>
 </div>
 ))}
 </div>

 {/* Platform health cards */}
 <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
 {/* Health score */}
 <div className="bg-white rounded-2xl p-5 border border-neutral-100 shadow-sm">
 <h3 className="text-sm font-semibold text-neutral-500 mb-3">Platform Health</h3>
 <div className="flex items-end gap-2">
 <span className={`text-4xl font-black ${healthScore >= 70 ? 'text-brand-600' : healthScore >= 40 ? 'text-amber-500' : 'text-red-500'}`}>
 {healthScore}
 </span>
 <span className="text-neutral-400 text-sm mb-1">/100</span>
 </div>
 <div className="mt-3 h-2 bg-neutral-100 rounded-full overflow-hidden">
 <div
 className={`h-full rounded-full transition-all ${healthScore >= 70 ? 'bg-brand-500' : healthScore >= 40 ? 'bg-amber-400' : 'bg-red-400'}`}
 style={{ width: `${healthScore}%` }}
 />
 </div>
 <p className="text-xs text-neutral-400 mt-2">
 {healthScore >= 70 ? ' Healthy — platform growing' : healthScore >= 40 ? ' Growing — keep acquiring users' : ' Early stage — focus on onboarding'}
 </p>
 </div>

 {/* Fill rate */}
 <div className="bg-white rounded-2xl p-5 border border-neutral-100 shadow-sm">
 <h3 className="text-sm font-semibold text-neutral-500 mb-3">Shift Fill Rate</h3>
 <div className="flex items-end gap-2">
 <span className={`text-4xl font-black ${fillRate >= 60 ? 'text-brand-600' : 'text-amber-500'}`}>{fillRate}%</span>
 </div>
 <div className="mt-3 h-2 bg-neutral-100 rounded-full overflow-hidden">
 <div className="h-full bg-brand-500 rounded-full" style={{ width: `${fillRate}%` }} />
 </div>
 <p className="text-xs text-neutral-400 mt-2">
 {stats?.total_completed || 0} of {stats?.total_applications || 0} applications completed
 </p>
 </div>

 {/* Supply / Demand */}
 <div className="bg-white rounded-2xl p-5 border border-neutral-100 shadow-sm">
 <h3 className="text-sm font-semibold text-neutral-500 mb-3">Supply / Demand Ratio</h3>
 <div className="flex items-end gap-2">
 <span className="text-4xl font-black text-blue-600">{workerEmployerRatio}</span>
 <span className="text-neutral-400 text-sm mb-1">workers/employer</span>
 </div>
 <p className="text-xs text-neutral-400 mt-4">Target: 5+ workers per employer for healthy marketplace</p>
 </div>
 </div>

 {/* Platform advantages */}
 <div className="bg-white rounded-2xl p-5 border border-neutral-100 shadow-sm">
 <h3 className="font-semibold text-neutral-900 mb-4"> Competitive Advantages Active</h3>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
 {[
 'Total pay shown on every shift card (not just hourly rate)',
 'Industry tags on all shifts — multi-sector coverage',
 'New worker friendly — no rating penalty on first shifts',
 'Crystal-clear application status — no ambiguity for workers',
 'One-tap apply — no multi-step forms or CV upload',
 'Instant confirmation to workers when hired',
 'Escrow payment protection (15% platform fee)',
 'Australia-wide rollout — starting strong in Sydney',
 ].map(text => (
 <div key={text} className="flex items-start gap-2 text-sm">
 <span className="text-brand-600 mt-0.5 shrink-0">✓</span>
 <span className="text-neutral-600">{text}</span>
 </div>
 ))}
 </div>
 </div>
 </>
 )}

 {/* ══ USERS TAB ══ */}
 {tab === 'users' && (
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div className="bg-white rounded-2xl p-6 border border-neutral-100 shadow-sm">
 <div className="flex items-center gap-3 mb-4">
 
 <div>
 <p className="font-bold text-neutral-900 text-lg">Workers</p>
 <p className="text-sm text-neutral-500">Registered on platform</p>
 </div>
 </div>
 <div className="text-5xl font-black text-blue-600">{stats?.total_workers || 0}</div>
 <div className="mt-4 pt-4 border-t border-neutral-100">
 <p className="text-sm text-neutral-500">Target for Q2: <span className="font-semibold text-neutral-700">100 workers</span></p>
 <div className="mt-2 h-2 bg-neutral-100 rounded-full overflow-hidden">
 <div className="h-full bg-blue-400 rounded-full" style={{ width: `${Math.min(100, ((stats?.total_workers || 0) / 100) * 100)}%` }} />
 </div>
 <p className="text-xs text-neutral-400 mt-1">{Math.round(((stats?.total_workers || 0) / 100) * 100)}% of target</p>
 </div>
 </div>

 <div className="bg-white rounded-2xl p-6 border border-neutral-100 shadow-sm">
 <div className="flex items-center gap-3 mb-4">
 
 <div>
 <p className="font-bold text-neutral-900 text-lg">Employers</p>
 <p className="text-sm text-neutral-500">Registered on platform</p>
 </div>
 </div>
 <div className="text-5xl font-black text-purple-600">{stats?.total_employers || 0}</div>
 <div className="mt-4 pt-4 border-t border-neutral-100">
 <p className="text-sm text-neutral-500">Target for Q2: <span className="font-semibold text-neutral-700">20 employers</span></p>
 <div className="mt-2 h-2 bg-neutral-100 rounded-full overflow-hidden">
 <div className="h-full bg-purple-400 rounded-full" style={{ width: `${Math.min(100, ((stats?.total_employers || 0) / 20) * 100)}%` }} />
 </div>
 <p className="text-xs text-neutral-400 mt-1">{Math.round(((stats?.total_employers || 0) / 20) * 100)}% of target</p>
 </div>
 </div>
 </div>
 )}

 {/* ══ SHIFTS TAB ══ */}
 {tab === 'shifts' && (
 <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm overflow-hidden">
 <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between">
 <h3 className="font-semibold text-neutral-900">All Shifts</h3>
 <span className="text-sm text-neutral-400">{shifts.length} total</span>
 </div>
 {shifts.length === 0 ? (
 <div className="text-center text-neutral-400 py-10">No shifts posted yet</div>
 ) : (
 <div className="overflow-x-auto">
 <table className="w-full text-sm">
 <thead>
 <tr className="text-left text-xs text-neutral-400 uppercase border-b border-neutral-100 bg-neutral-50">
 <th className="px-5 py-3">Shift</th>
 <th className="px-3 py-3">Industry</th>
 <th className="px-3 py-3">Location</th>
 <th className="px-3 py-3">Pay</th>
 <th className="px-3 py-3">Status</th>
 </tr>
 </thead>
 <tbody>
 {shifts.map(shift => (
 <tr key={shift.id} className="border-b border-neutral-50 hover:bg-neutral-50 transition">
 <td className="px-5 py-3 font-medium text-neutral-900">{shift.title}</td>
 <td className="px-3 py-3 text-neutral-500">{shift.industry || '—'}</td>
 <td className="px-3 py-3 text-neutral-500">{shift.location || '—'}</td>
 <td className="px-3 py-3">
 <span className="text-brand-600 font-bold">
 ↑ ${((shift.hourly_rate || 0) * (shift.hours || 0)).toFixed(0)}
 </span>
 <span className="text-neutral-400 text-xs ml-1">(${shift.hourly_rate}/hr × {shift.hours}h)</span>
 </td>
 <td className="px-3 py-3">
 <span className={`text-xs px-2 py-1 rounded-full border font-semibold ${
 shift.status === 'open' ? 'bg-brand-50 text-brand-700 border-brand-200' :
 shift.status === 'filled' ? 'bg-blue-50 text-blue-700 border-blue-200' :
 'bg-neutral-100 text-neutral-500 border-neutral-200'
 }`}>{(shift.status || 'open').toUpperCase()}</span>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </div>
 )}
 {tab === 'metrics' && (
 <div className="space-y-5">
 <h2 className="text-lg font-bold text-neutral-900">Platform Metrics</h2>
 {!metrics ? (
 <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div></div>
 ) : (
 <>
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
 {[
 { label: 'Total Shifts', value: metrics.total_shifts, icon: '' },
 { label: 'Applications', value: metrics.total_applications, icon: '' },
 { label: 'Hires', value: metrics.total_hires, icon: '✅' },
 { label: 'Fill Rate', value: `${metrics.fill_rate || 0}%`, icon: '' },
 { label: 'Workers', value: metrics.total_workers, icon: '' },
 { label: 'Employers', value: metrics.total_employers, icon: '' },
 { label: 'Platform Revenue', value: `$${parseFloat(metrics.platform_revenue || 0).toFixed(2)}`, icon: '' },
 { label: 'Avg Rating', value: metrics.avg_rating ? `${metrics.avg_rating} ⭐` : 'N/A', icon: '' },
 ].map(m => (
 <div key={m.label} className="bg-white rounded-xl p-4 border border-neutral-100 shadow-sm">
 <div className="text-2xl mb-1">{m.icon}</div>
 <div className="text-xl font-bold text-neutral-900">{m.value}</div>
 <div className="text-xs text-neutral-400">{m.label}</div>
 </div>
 ))}
 </div>
 <div className="bg-white rounded-xl p-5 border border-neutral-100 shadow-sm">
 <h3 className="font-semibold text-neutral-900 mb-3">Pending Cert Reviews ({pendingCerts.length})</h3>
 {pendingCerts.length === 0 ? (
 <p className="text-neutral-400 text-sm">No pending certifications</p>
 ) : (
 <div className="space-y-3">
 {pendingCerts.map(cert => (
 <div key={cert.id} className="flex items-center justify-between bg-neutral-50 rounded-lg p-3">
 <div>
 <p className="font-medium text-neutral-900 text-sm">{cert.cert_type}</p>
 <p className="text-xs text-neutral-500">{cert.cert_number} · {cert.issuing_body}</p>
 </div>
 <div className="flex gap-2">
 <button onClick={() => handleCertVerify(cert.id, true)} disabled={certAction === cert.id} className="text-xs bg-brand-600 text-white px-3 py-1.5 rounded-lg font-medium">✓ Approve</button>
 <button onClick={() => handleCertVerify(cert.id, false)} disabled={certAction === cert.id} className="text-xs bg-red-100 text-red-700 px-3 py-1.5 rounded-lg font-medium">✗ Reject</button>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 </>
 )}
 </div>
 )}

 {/* ══ REPORTS TAB ══ */}
 {tab === 'reports' && <ReportsPanel http={http} />}
 </>
 )}
 </div>

 <footer className="border-t border-neutral-100 bg-white mt-8">
 <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between text-[11px] text-neutral-400">
 <span>&copy; 2026 ShiftMatch</span>
 <a href="/privacy.html" className="hover:text-neutral-600">Privacy</a>
 </div>
 </footer>
 </div>
 );
}

// Reports management panel
function ReportsPanel({ http }) {
 const [reports, setReports] = React.useState([]);
 const [loading, setLoading] = React.useState(true);
 const [resolving, setResolving] = React.useState(null);
 const [notes, setNotes] = React.useState('');

 React.useEffect(() => { loadReports(); }, []);

 const loadReports = async () => {
 try { const r = await http.get('/admin/reports'); setReports(r.data || []); } catch { /* silent */ }
 setLoading(false);
 };

 const resolveReport = async (id, status, addStrike) => {
 setResolving(id);
 try {
 await http.put(`/admin/reports/${id}`, { status, admin_notes: notes, add_strike: addStrike });
 setNotes('');
 loadReports();
 } catch { /* silent */ }
 setResolving(null);
 };

 const SEVERITY_BADGE = {
 critical: 'bg-red-100 text-red-700 border-red-200',
 high: 'bg-orange-100 text-orange-700 border-orange-200',
 medium: 'bg-amber-50 text-amber-700 border-amber-200',
 low: 'bg-neutral-100 text-neutral-500 border-neutral-200',
 };

 if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div></div>;

 return (
 <div className="space-y-4">
 <div className="flex items-center justify-between">
 <h2 className="text-lg font-bold text-neutral-900"> User Reports</h2>
 <span className="text-xs text-neutral-400">{reports.filter(r => r.status === 'pending').length} pending</span>
 </div>

 {reports.length === 0 ? (
 <div className="text-center py-12 bg-white rounded-xl border border-neutral-100">
 <p className="text-neutral-400">No reports yet</p>
 </div>
 ) : reports.map(r => (
 <div key={r.id} className={`bg-white rounded-xl border p-4 shadow-sm ${
 r.status === 'pending' ? 'border-amber-200' : 'border-neutral-100'
 }`}>
 <div className="flex items-start justify-between gap-3 mb-2">
 <div>
 <div className="flex items-center gap-2 flex-wrap">
 <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold ${SEVERITY_BADGE[r.severity] || SEVERITY_BADGE.low}`}>
 {(r.severity || 'low').toUpperCase()}
 </span>
 <span className="text-[10px] px-2 py-0.5 bg-neutral-100 text-neutral-600 rounded-full">{r.reported_role}</span>
 <span className="text-[10px] text-neutral-400">{r.reason?.replace(/_/g, ' ')}</span>
 </div>
 <p className="font-semibold text-neutral-900 text-sm mt-1">{r.reported_name} ({r.reported_email})</p>
 <p className="text-xs text-neutral-500 mt-0.5">{r.description?.substring(0, 200)}</p>
 <div className="flex gap-3 mt-1 text-[10px] text-neutral-400">
 <span>Strikes: <strong className={r.strike_count >= 2 ? 'text-red-600' : ''}>{r.strike_count}/3</strong></span>
 <span>Evidence: {r.evidence_count}</span>
 <span>{new Date(r.created_at).toLocaleDateString('en-AU')}</span>
 </div>
 </div>
 <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
 r.status === 'pending' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
 r.status === 'resolved' ? 'bg-brand-50 text-brand-700 border border-brand-200' :
 r.status === 'dismissed' ? 'bg-neutral-100 text-neutral-500 border border-neutral-200' :
 'bg-blue-50 text-blue-700 border border-blue-200'
 }`}>{r.status}</span>
 </div>

 {r.status === 'pending' && (
 <div className="mt-3 pt-3 border-t border-neutral-100">
 <input value={resolving === r.id ? notes : ''} onChange={e => { setResolving(r.id); setNotes(e.target.value); }}
 placeholder="Admin notes..."
 className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-xs mb-2" />
 <div className="flex gap-2">
 <button onClick={() => resolveReport(r.id, 'resolved', true)}
 className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-bold">Resolve + Strike</button>
 <button onClick={() => resolveReport(r.id, 'resolved', false)}
 className="px-3 py-1.5 bg-brand-500 text-white rounded-lg text-xs font-bold">Resolve (no strike)</button>
 <button onClick={() => resolveReport(r.id, 'dismissed', false)}
 className="px-3 py-1.5 bg-neutral-200 text-neutral-600 rounded-lg text-xs font-bold">Dismiss</button>
 </div>
 </div>
 )}
 </div>
 ))}

    </div>
  );
}

// Footer for AdminDashboard is rendered in the main component above
